<template>
  <div class="flex h-screen overflow-hidden">
    <sidebar-submenu
      v-if="isNotViewWithoutSidebar"
      v-model:active-tab="activeTab"
      :options="buildersOptions"
      has-tabs
      searchable
    />
    <div class="h-full w-full overflow-auto">
      <router-view />
    </div>
  </div>
</template>
<script setup lang="ts">
import { watchImmediate } from "@vueuse/core";
import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import compact from "lodash/compact";

import { Ability, PermissionSubject } from "@/enums/auth";
import {
  ADMIN,
  CLIENT_ADMIN,
  CLIENT_FUNDING_ADVISOR,
  CLIENT_ROLE_GROUP,
  CLIENT_UNDERWRITER,
  FUNDING_ADVISOR,
  LENDFLOW_ROLE_GROUP,
  SUPER_ADMIN,
  UNDERWRITER,
} from "@/helpers/constants";
import { BuildersViewActiveTab } from "@/helpers/symbols";
import { useAuth, usePermissions } from "@/hooks/auth";
import {
  ROUTE_BUILDERS_BASE,
  ROUTE_CONTRACT_BUILDER_EDIT,
  ROUTE_CUSTOM_ATTRIBUTES_BUILDER_VIEW,
  ROUTE_ORCHESTRATION_TEMPLATE,
} from "@/router/routes";

import IconAttributeBuilder from "@/components/icons/IconAttributeBuilder.vue";
import IconBorrowerPlatformBuilder from "@/components/icons/IconBorrowerPlatformBuilder.vue";
import IconCheckmarkDouble from "@/components/icons/IconCheckmarkDouble.vue";
import IconCheckoutBuilder from "@/components/icons/IconCheckoutBuilder.vue";
import IconCommandBarSidebar from "@/components/icons/IconCommandBarSidebar.vue";
import IconFileSmall from "@/components/icons/IconFileSmall.vue";
import IconMessage from "@/components/icons/IconMessage.vue";
import IconWebhook from "@/components/icons/IconWebhook.vue";
import IconEmailIntegration from "@/components/icons/sidebar/IconEmailIntegration.vue";
import IconContractBuilder from "@/components/icons/sidebar/profile/IconContractBuilder.vue";
import IconWorkflow from "@/components/icons/sidebar/profile/IconWorkflow.vue";
import SidebarSubmenu from "@/components/sidebar/SidebarSubmenu.vue";

import type { IClient } from "@/models/clients";
import type {
  ISidebarSubmenuOption,
  ISidebarSubmenuSection,
} from "@/models/common";
import type { Role } from "@/models/options";

const activeTab = ref("");
provide(BuildersViewActiveTab, activeTab);
const auth = useAuth();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { getters } = useStore();
const { canPerformActionReactive } = usePermissions();

const canViewScoreCards = canPerformActionReactive(
  PermissionSubject.scoreCard,
  Ability.viewAny,
  {}
);

const canViewConsentTemplates = canPerformActionReactive(
  PermissionSubject.consentTemplate,
  Ability.viewAny,
  {}
);

const authClient = computed<IClient>(() => getters["auth/authClientSettings"]);

const isNotViewWithoutSidebar = computed<boolean>(
  () =>
    !(
      [
        ROUTE_ORCHESTRATION_TEMPLATE,
        ROUTE_CUSTOM_ATTRIBUTES_BUILDER_VIEW,
        ROUTE_CONTRACT_BUILDER_EDIT,
      ] as string[]
    ).includes(route.name!.toString())
);

const scorecardTabs = [
  t("COMMON.BUILDER"),
  t("SIDEBAR.REPORTS"),
  t("SCORECARDS.SC_BACKTEST_REPORTS"),
];

const alignActiveTabWithRoute = () => {
  if (route.name === "Scorecards") {
    if (!scorecardTabs.includes(activeTab.value)) {
      activeTab.value = scorecardTabs[0];
    }
    return;
  }

  activeTab.value = "";
};

const operatingSystemAcls = computed(() => {
  const acls: Record<string, Role[]> = {
    ALL_ROLES: [...LENDFLOW_ROLE_GROUP, ...CLIENT_ROLE_GROUP],
    ORCHESTRATION: [
      ADMIN,
      SUPER_ADMIN,
      FUNDING_ADVISOR,
      UNDERWRITER,
      CLIENT_ADMIN,
      CLIENT_UNDERWRITER,
      CLIENT_FUNDING_ADVISOR,
    ],
    CONTRACT_BUILDER: compact([
      ...LENDFLOW_ROLE_GROUP,
      (authClient.value?.can_manage_docusign && CLIENT_ADMIN) || null,
    ]),
    COMMAND_BAR_SETTINGS: LENDFLOW_ROLE_GROUP,
  };

  return acls;
});

const operatingSystemOptions = computed<
  (ISidebarSubmenuOption & { visible: () => boolean })[]
>(() =>
  [
    {
      id: "workflow",
      icon: IconWorkflow,
      label: "Workflow",
      link: "/builders/workflow",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () =>
        !auth.isClient || authClient.value?.can_manage_workflow_builder,
    },
    {
      id: "borrower-platform",
      icon: IconBorrowerPlatformBuilder,
      label: t("BORROWER_PLATFORM.TITLE"),
      link: "/builders/borrower-platform-templates",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () => true,
    },
    {
      id: "documents",
      icon: IconFileSmall,
      label: t("COMMON.DOCUMENTS"),
      link: "/builders/documents",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () => true,
    },
    {
      id: "attributes",
      icon: IconAttributeBuilder,
      label: t("ATTRIBUTE_BUILDER.ATTRIBUTES"),
      link: "/builders/attributes",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () => true,
    },
    {
      id: "checkout",
      icon: IconCheckoutBuilder,
      label: t("CHECKOUT.CHECKOUT"),
      link: "/builders/checkout",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () => true,
    },
    {
      id: "consent-templates",
      icon: IconCheckmarkDouble,
      label: t("CUSTOMIZE.CONSENT.TITLE"),
      link: "/builders/consent-templates",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () => canViewConsentTemplates.value,
    },
    {
      id: "orchestration",
      icon: "orchestration-color",
      label: "Data Orchestration",
      link: "/builders/orchestration",
      acl: operatingSystemAcls.value.ORCHESTRATION,
      visible: () =>
        !auth.isClient || authClient.value?.can_manage_data_orchestration,
    },
    {
      id: "contracts",
      icon: IconContractBuilder,
      label: "Contracts",
      link: "/builders/contracts",
      acl: operatingSystemAcls.value.CONTRACT_BUILDER,
      visible: () => true,
    },
    {
      id: "scorecards",
      icon: "scorecards",
      label: t("SCORECARDS.TITLE"),
      link: "/builders/scorecards",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () =>
        canViewScoreCards.value &&
        (!auth.isClient || authClient.value?.can_manage_score_cards),
      tabs: scorecardTabs,
      width: "16",
      height: "16",
    },
    {
      id: "custom-webhooks",
      icon: IconWebhook,
      label: "Webhooks",
      link: "/builders/custom-webhooks",
      acl: operatingSystemAcls.value.ALL_ROLES,
      visible: () => true,
    },
    {
      id: "command-bar",
      icon: IconCommandBarSidebar,
      label: t("PROFILE.COMMAND_BAR"),
      link: "/builders/command-bar",
      acl: operatingSystemAcls.value.COMMAND_BAR_SETTINGS,
      visible: () => !auth.isClient,
    },
  ].map((option, index) => ({
    ...option,
    order: index + 1,
  }))
);

const communicationOptions = computed<
  (ISidebarSubmenuOption & { visible: () => boolean })[]
>(() => [
  {
    id: "email-templates",
    icon: IconEmailIntegration,
    label: "Email",
    link: "/builders/email-templates",
    acl: operatingSystemAcls.value.ALL_ROLES,
    visible: () =>
      !auth.isClient || authClient.value?.has_custom_emails_enabled,
  },
  {
    id: "sms-templates",
    icon: IconMessage,
    label: "SMS",
    link: "/builders/sms-templates",
    acl: operatingSystemAcls.value.ALL_ROLES,
    visible: () => !auth.isClient,
  },
]);

const underwritingOptions = computed(() => {
  return operatingSystemOptions.value.filter((config) => config.visible());
});

const communicationMenuOptions = computed(() => {
  return communicationOptions.value.filter((config) => config.visible());
});

const buildersOptions = computed((): ISidebarSubmenuSection[] => [
  {
    name: "Operating System",
    options: underwritingOptions.value,
  },
  {
    name: "Communication",
    options: communicationMenuOptions.value,
  },
]);

watchImmediate([() => route.name, buildersOptions], alignActiveTabWithRoute);

watchImmediate(
  () => route.name,
  () => {
    // Redirect if we're on the base builders route
    if (route.name === ROUTE_BUILDERS_BASE) {
      const firstVisibleOption = underwritingOptions.value[0];
      if (firstVisibleOption?.link) {
        router.replace(firstVisibleOption.link);
      }
    }
  }
);
</script>
